import React, { useCallback, useState, Fragment } from 'react';
import PropTypes from 'prop-types';
import window from 'window-or-global';
import { ReactReduxContext } from 'react-redux';
import { ConnectedRouter } from 'connected-react-router';
import { nativeEditorComponentsPreloader } from '@confluence-classic/confluence-fabric-editor/src/lib';

import { isEmbeddedConfluence_DO_NOT_USE } from '@atlassian/embedded-confluence/isEmbeddedConfluence';

import { ConnectSupport } from '@confluence/connect-support';
import { RouteManager } from '@confluence/route-manager/entry-points/RouteManager';
import { useNavigationPolicy } from '@confluence/route-manager/entry-points/useNavigationPolicy';
import { isViewPageLite } from '@confluence/view-page/entry-points/isViewPageLite';
import { namedRoutes, isEditPage } from '@confluence/named-routes';
import { Attribution, ErrorBoundary } from '@confluence/error-boundary';
import {
	startBrowserMetricsPageLoad,
	markCustomTransitionExperienceForRoute,
	BMEventBus,
	EMBEDDED_EDIT_FMP_ACTION,
	EMBEDDED_EDIT_TTI_ACTION,
	TimeToAppIdle,
} from '@confluence/browser-metrics';
import { setUniquePageLoadId } from '@confluence/unique-page-load-id';
import { CopySpacePermissionsForLegacyNewSpace } from '@confluence/copy-space-permissions';
import { Flags } from '@confluence/flags';
import { CreateDialogContextProvider } from '@confluence/create-dialog-context';
import { Dialogs } from '@confluence/dialogs';
import { NetworkStatusListener } from '@confluence/network-status-listener';
import { Pendo } from '@confluence/pendo';
import { HighlightActionsProviderForNav3 } from '@confluence/highlight-actions/entry-points/HighlightActionsProvider';
import {
	debuggerEnabled,
	clearAnalyticsDebugger,
} from '@confluence/analytics-debugger/entry-points/AnalyticsDebugger';
import { getBrowserHistory, setBrowserHistoryListener } from '@confluence/browser-history';
import { startExperienceForRoute } from '@confluence/experience-tracker';
import { getMonitoringClient } from '@confluence/monitoring';
import { releaseSSRedLegacyMacros } from '@confluence/content-renderer-legacy-macros';
import { ExternalShareContextProvider } from '@confluence/external-share-context';
import { Heartbeat } from '@confluence/heartbeat';
import { EditPageLoadingContextProvider } from '@confluence/load-edit-page/entry-points/EditPageLoadingContext';
import { LocalStorageQuotaSubscriber } from '@confluence/local-storage-quota-subscriber';
import { useInitializeThemeStateless } from '@confluence/theming/entry-points/useInitializeThemeStateless';
import { NotificationLandedEvent } from '@confluence/notifications/entry-points/NotificationLandedEvent';
import { ExperienceActionMeasures } from '@confluence/action-measures';
import { Cookies } from '@confluence/cookies';
import { resetApolloStats } from '@confluence/graphql';
import { preloadQueryBasedOnRoute, preloadExtensionsData } from '@confluence/query-preloaders';

import { routeComponents, NotFound } from '../Routes';
import { LazyCreateDialogQueryRenderer } from '../CreateDialogQueryRenderer';
import { LazyCreateDialogBlueprintWizardRenderer } from '../PseudoWizardCreateDialog';
import LegacyBridge from '../LegacyBridge';
import SingleModalContainer from '../SingleModal';
import LegacyCssOverrides from '../../tech-debt/LegacyCssOverrides';
import ShortcutsProvider from '../KeyboardShortcuts'; // 0KB
import ChangeboardingManager from '../Changeboarding/ChangeboardingManager'; // 240KB
import EngagementMessages from '../EngagementMessages';
import { preloadComponents } from '../../route-component-preloader';
// This is tangled with fabric editor state that we can't remove for now.
import ErrorContainer from '../ReduxError';

const browserHistory = getBrowserHistory();

/**
 * Note: this wont run on legacy .action routes
 * if you are adding a global component that needs to work on legacy routes, you may need to add it to StandaloneConfluenceNavigation as well
 */

export function App({ sessionData }) {
	window.performance.mark('CFP-63.app-component-render');

	const [shouldForceUpdate, setShouldForceUpdate] = useState(false);
	const { routePolicy } = useNavigationPolicy();

	Cookies.initialize();

	// Initializing the theme in a non-blocking way, instead of using <ThemeContainer>, so
	// that the app does not flash and re-paint after the SPA loads on top of the SSR page.
	useInitializeThemeStateless();

	// Lite Mode™ check outside of the router
	// For certain pages supporting lite mode some functionality is not needed
	const isLite = isViewPageLite();

	const onRoute = useCallback(
		async (match, transitionId) => {
			// Reset window.__APOLLO_STATS__ on transition
			resetApolloStats();

			// theoretically, `match` cannot be null on transitions.
			// This code path should also be unreachable during initial loads.
			// But for the sake of being 100% error-proof, we're ensuring that
			// `match` exists.
			if (shouldForceUpdate && match) {
				// at this point, `window.location.href` hasn't been updated yet.
				// So instead of doing `window.location.reload()` we're explicitly
				// navigating the page to `match.url`.
				// eslint-disable-next-line no-restricted-syntax
				window.location.assign(match.url);

				// early exit to not even try and load the route
				return;
			}

			// transitionId = 0 when it is initial load, and 0 is falsey
			if (transitionId) {
				// The page can't be rendered by SSR if is a transition
				window['__SSR_RENDERED__'] = false;
				releaseSSRedLegacyMacros();
				if (debuggerEnabled) {
					clearAnalyticsDebugger();
				}
			} else {
				window.performance.mark('CFP-63.route-manager-initialized');
			}
			setUniquePageLoadId(match?.name ?? '', match, transitionId, sessionData);

			startExperienceForRoute(match, transitionId, sessionData);

			void BMEventBus.stream([{ type: 'page-transition', ID: transitionId }]);
			//if the new editor metrics FF is on, start BM here only if the route is not edit page or if it is initial load, or an embedded editor.
			if (!isEditPage(match?.name) || transitionId === 0 || isEmbeddedConfluence_DO_NOT_USE()) {
				startBrowserMetricsPageLoad(
					match?.name ?? '',
					match?.ufoName,
					transitionId,
					match?.params.contentId,
				);
				//If it is an embedded editor, start the BM right here since it does a full page relaod
				if (isEditPage(match?.name) && isEmbeddedConfluence_DO_NOT_USE()) {
					ExperienceActionMeasures.markMeasureStart(EMBEDDED_EDIT_FMP_ACTION);
					ExperienceActionMeasures.markMeasureStart(EMBEDDED_EDIT_TTI_ACTION);
				}
			}

			// mark edit experience after transition to new route is complete
			markCustomTransitionExperienceForRoute(match?.name);

			getMonitoringClient().updateContext({
				pageName: match?.name ?? 'unknown',
				transitionId,
				product: isEmbeddedConfluence_DO_NOT_USE() ? 'embeddedConfluence' : 'confluence',
			});

			const { forceUpdateCheck } = await import(
				/* webpackChunkName: "loadable-confluenceforce-update-check" */ '@confluence/force-update-check'
			);
			const val = await forceUpdateCheck();
			if (val) {
				// do not reload the page just yet -- the SPA transition has taken place already.
				// Instead of having the user go through "SPA transition --> full page reload" sequence,
				// we'll wait until the next transition, and only then perform a full page refresh.
				setShouldForceUpdate(true);
			}
		},
		[sessionData, shouldForceUpdate, setShouldForceUpdate],
	);

	/**
	 * Please note this App is a pure component.
	 * This means following components will NOT re-render when user navigates.
	 * Please be careful when adding any component that requires router information.
	 */
	return (
		<ErrorBoundary attribution={Attribution.BACKBONE}>
			<ChangeboardingManager>
				<ConnectedRouter history={browserHistory} context={ReactReduxContext}>
					{/* Components above this line do NOT re-render in SPA transition */}
					<Fragment>
						<LegacyBridge />
						{/* This needs be placed before content */}
						<ExternalShareContextProvider path={browserHistory.location.pathname}>
							<CreateDialogContextProvider>
								{/* nativeEditorComponentsPreloader need to be passed here to avoid circular dependencies in full page editor */}
								<EditPageLoadingContextProvider
									nativeEditorComponentsPreloader={nativeEditorComponentsPreloader}
								>
									<RouteManager
										routeComponents={routeComponents}
										namedRoutes={namedRoutes}
										notFound={NotFound}
										onInitialize={onRoute}
										onTransition={onRoute}
										onPreload={onPreloadRoute}
										routePolicy={routePolicy}
										setBrowserHistoryListener={setBrowserHistoryListener}
									>
										{/* Components that relies on RoutesContext */}
										<LegacyCssOverrides />
										<Heartbeat />
										<NotificationLandedEvent />
										<TimeToAppIdle />

										{!isLite && (
											<>
												<Dialogs />
												<LazyCreateDialogQueryRenderer />
												<LazyCreateDialogBlueprintWizardRenderer />
											</>
										)}
									</RouteManager>
								</EditPageLoadingContextProvider>
							</CreateDialogContextProvider>
						</ExternalShareContextProvider>
						<HighlightActionsProviderForNav3 />
						<SingleModalContainer />
						<NetworkStatusListener />
					</Fragment>
					{/* Components after this line do NOT re-render in SPA transition */}
				</ConnectedRouter>
				<ShortcutsProvider />
				<ErrorContainer />
				<ConnectSupport />
				<Pendo />
				<Flags />
				<LocalStorageQuotaSubscriber />

				{!isLite && (
					<>
						<CopySpacePermissionsForLegacyNewSpace />
						<EngagementMessages />
					</>
				)}
			</ChangeboardingManager>
		</ErrorBoundary>
	);
}

App.propTypes = {
	sessionData: PropTypes.object.isRequired,
};

function onPreloadRoute({ url }, previousMatch) {
	void preloadComponents(url, true);
	void preloadQueryBasedOnRoute(url, true, previousMatch);
	void preloadExtensionsData(url);
}
